import React from 'react';
import { AxiosError } from 'axios';

import { AutocompleteRenderInputParams, FormControl, FormHelperText } from '@mui/material';

import Chip from 'components/Chip';
import { StyledTextField } from 'components/Input/styles';
import InfiniteAutocomplete from 'components/InfiniteAutocomplete';

import { getEntityTranslation } from 'stores/EntityDetails/utils';
import { EntityModules } from 'core/constants';

import API from 'utils/api';
import { isForbidden } from 'utils/api/errors';
import { SearchRequest, UuidableName } from 'utils/api/types';
import { useInfiniteSearch } from 'utils/hooks/infiniteSearch';

import { LookupAnswerEntry, lookupEntryUuids, rememberLookupRecords, resolveLookupEntries } from '../lookupRecords';
import { StyledOption } from './styles';

export type AuditsLookupFieldProps = {
  module: EntityModules;
  value: LookupAnswerEntry[];
  onChange: (records: UuidableName[]) => void | Promise<unknown>;
  excludeUuids?: string[];
  /** "Search <records>" rather than "Search or select <records>". */
  shortPlaceholder?: boolean;
  disabled?: boolean;
  requiredNotFilled?: boolean;
  errorMessage?: string;
  fieldKey?: string;
};

const uuidKey = (entries: LookupAnswerEntry[]): string => lookupEntryUuids(entries).join(' ');

/** Multi-record chip picker for a `multiple` audits lookup question, and the Related records card's picker. */
const AuditsLookupField = ({
  module,
  value,
  onChange,
  excludeUuids = [],
  shortPlaceholder = false,
  disabled = false,
  requiredNotFilled,
  errorMessage,
  fieldKey = 'lookup',
}: AuditsLookupFieldProps): React.ReactElement => {
  // Mirrors the uncontrolled input, so a search still waiting out its debounce reads as loading.
  const [inputValue, setInputValue] = React.useState('');
  // Set on a 403 (no licence for the type): the list reads as empty, as in Events, and typing stops searching.
  const forbiddenModule = React.useRef<EntityModules | null>(null);

  const request = React.useCallback(
    (params?: SearchRequest) => {
      const search = API.entities.searchEntity(module, undefined, { ...params, ordering: `${module}__name` });
      const call = () =>
        search().catch((error: AxiosError) => {
          if (!isForbidden(error) || !error.response) throw error;
          forbiddenModule.current = module;
          return { ...error.response, data: [] };
        });
      call.config = search.config;
      return call;
    },
    [module]
  );

  const { results, loading, loadingMore, hasMore, loadMore, appliedQuery, failed, searchQuery, retry } =
    useInfiniteSearch<UuidableName>(request);

  React.useEffect(() => {
    rememberLookupRecords(results);
  }, [results]);

  // Autosave is debounced 1000 ms, so the prop lags the selection: without local state a second pick
  // inside that window derives from the stale prop and drops the first record.
  const [selection, setSelection] = React.useState<UuidableName[]>(() => resolveLookupEntries(value));

  // Reconciled on uuids, not prop identity: the store replaces `data` wholesale on every autosave
  // response, so matching uuids leave our labelled chips alone when the bare-uuid echo arrives.
  const propUuids = uuidKey(value || []);
  React.useEffect(() => {
    setSelection((current) => (propUuids === uuidKey(current) ? current : resolveLookupEntries(value)));
  }, [propUuids]);

  const { singular, plural } = getEntityTranslation[module] || { singular: 'record', plural: 'records' };
  // Plural, unlike EntitySearchField's "a site": several records can be picked here.
  const placeholder = `${shortPlaceholder ? 'Search' : 'Search or select'} ${plural.toLowerCase()}`;

  // Clearing a required answer deletes the stored field error, so without this it goes red silently.
  const missingRequired = Boolean(requiredNotFilled) && selection.length === 0;

  const excluded = new Set([...excludeUuids, ...selection.map((record) => record.uuid)]);
  const options = results.filter((record) => !excluded.has(record.uuid));

  // A forbidden type never searches, so its typed text must not read as a pending search.
  const searchPending = forbiddenModule.current !== module && inputValue !== appliedQuery;

  const handleSelect = async (records: UuidableName[]): Promise<void> => {
    rememberLookupRecords(records);
    setSelection(records);

    await onChange(records);
  };

  const renderInput = (inputParams: AutocompleteRenderInputParams): React.ReactElement => {
    const { InputProps, ...restParams } = inputParams;

    return (
      <StyledTextField
        {...restParams}
        error={missingRequired || !!errorMessage || failed}
        helperText={errorMessage}
        InputProps={{ ...InputProps, type: 'search' }}
        placeholder={placeholder}
      />
    );
  };

  return (
    <FormControl fullWidth error={missingRequired || failed}>
      <InfiniteAutocomplete
        multiple
        loadingMore={loadingMore}
        hasMore={hasMore}
        onLoadMore={loadMore}
        disableCloseOnSelect
        disabled={disabled}
        id={`${fieldKey}-lookup-field-${module}`}
        data-testid="audit-lookup-field"
        options={options}
        value={selection}
        loading={loading || searchPending}
        fullWidth
        filterSelectedOptions
        noOptionsText="No records found"
        getOptionLabel={(option) => (option as UuidableName)?.name || ''}
        isOptionEqualToValue={(option, selected) => (option as UuidableName).uuid === (selected as UuidableName).uuid}
        // Not the input's onChange: MUI empties the text itself on a pick, the clear button and blur.
        onInputChange={(_, newInputValue) => {
          setInputValue(newInputValue);
          if (forbiddenModule.current !== module) {
            searchQuery(newInputValue);
          }
        }}
        // Reopening is the "Try again" the load error asks for.
        onOpen={() => {
          if (failed) {
            retry();
          }
        }}
        renderOption={(props, option) => {
          const { uuid, name } = option as UuidableName;
          const { key: _key, ...rest } = props as React.HTMLAttributes<HTMLLIElement> & { key?: React.Key };

          return (
            <StyledOption key={uuid} {...rest}>
              {name}
            </StyledOption>
          );
        }}
        renderInput={renderInput}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              data-testid="audit-lookup-chip"
              label={(option as UuidableName)?.name || ''}
              tooltipTitle={singular}
              fontSize={14}
              variant="filled"
            />
          ))
        }
        onChange={(_, newValue) => {
          handleSelect((newValue as UuidableName[]) || []);
        }}
      />
      {missingRequired && !errorMessage && <FormHelperText>This field is required</FormHelperText>}
      {failed && <FormHelperText>Could not load records. Try again.</FormHelperText>}
    </FormControl>
  );
};

export default AuditsLookupField;
